import axios, { AxiosInstance } from "axios";
import { toast } from "sonner";
import {
  attachAxiosAuth,
  createSsoAuthClient,
  installVisibilityRefresh,
  localStorageTokenStore,
  type StartLoginOptions,
} from "@signaris/auth-client/browser";
import { getTargetTenant } from "@/state/targetTenant";
import type { DecodedPrincipal } from "./types";

// Классы ошибок либы реэкспортим отсюда: весь auth-рантайм NET живёт в одном
// модуле, а единственная точка импорта гарантирует и единственную идентичность
// классов для `instanceof` (см. AuthCallback).
export {
  MissingVerifierError,
  StorageUnavailableError,
  TokenExchangeError,
} from "@signaris/auth-client/browser";

// SSO-вход — единый оркестратор из @signaris/auth-client (Authorization Code +
// PKCE, silent refresh, logout). Локальных копий нет — баг чинится в либе.
const SIGNARIS_AUTH_URL =
  import.meta.env.VITE_SIGNARIS_AUTH_URL || "https://auth.signaris.ru";

const TOKEN_KEY = "signaris_net_access_token";
const EXPIRES_KEY = "signaris_net_access_expires_at";

const store = localStorageTokenStore({
  access: TOKEN_KEY,
  refresh: "signaris_net_refresh_token",
  accessExpires: EXPIRES_KEY,
});

const authClient = createSsoAuthClient({
  authBaseUrl: SIGNARIS_AUTH_URL,
  redirectUri: `${window.location.origin}/auth/callback`,
  store,
  // Корень NET (/ → Navigate /admin → guard уведёт на логин). НЕ /auth/login —
  // такого роута в NET нет, был бы React Router 404 после logout.
  logoutReturnTo: `${window.location.origin}/`,
  // 0.12+: вход, отложенный из скрытой страницы, отменяется, если к её показу
  // другая вкладка уже вошла (в общем localStorage лежит другой refresh-токен).
  // Без колбэка либа даже не сравнивает токены и всё равно уходит на auth, а
  // экран «Входим…» — терминальный: лоадер роутера уже отработал.
  onSessionRestored: () => window.location.reload(),
  // 0.12+: отложенный вход упал уже ПОСЛЕ resolve промиса — узнать иначе никак.
  onLoginError: reportLoginFailure,
});

// Проактивный refresh при возврате вкладки из фона (0.9.0) — иначе первый
// запрос после долгого сна ловит 401→refresh→retry с лишней задержкой.
installVisibilityRefresh(authClient);

/**
 * Единая раковина для «вход не начался»: `StorageUnavailableError` (браузер
 * запретил и sessionStorage, и localStorage) или падение отложенного входа.
 * Молчать нельзя — экран «Входим…» без этого висит без объяснений.
 * Function declaration, а не const: используется выше по файлу (hoisting).
 */
function reportLoginFailure(err: unknown): void {
  console.error("[net-auth] вход не начался", err);
  toast.error("Не удалось начать вход", {
    description: "Браузер блокирует хранилище сайта или сеть недоступна.",
    action: {
      label: "Повторить",
      onClick: () => beginLogin(undefined, { force: true }),
    },
  });
}

// ─── Sync-хелперы для роутер-гардов и UI (читают токен из localStorage) ──────

function ls(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function getAccessToken(): string | null {
  return ls(TOKEN_KEY);
}

export function getAccessExpiresAt(): string | null {
  return ls(EXPIRES_KEY);
}

export function isAuthenticated(): boolean {
  return Boolean(ls(TOKEN_KEY));
}

// ─── JWT decode (без verify — verify делает backend; UI просто читает claims) ──

export function decodeJwt(token: string): DecodedPrincipal | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")),
    );
    const productRoles = payload.product_roles ?? {};
    const netRole = productRoles.net ?? null;
    return {
      employee_id: payload.sub,
      email: payload.email,
      full_name: payload.full_name,
      tenant_id: payload.tenant_id,
      tenant_slug: payload.tenant_slug,
      net_role: netRole,
      is_superadmin:
        productRoles.signaris_auth === "superadmin" || netRole === "admin",
    };
  } catch {
    return null;
  }
}

export function getCurrentPrincipal(): DecodedPrincipal | null {
  const t = ls(TOKEN_KEY);
  return t ? decodeJwt(t) : null;
}

// ─── Login / callback / logout — делегируем общему оркестратору ──────────────

/**
 * Старт логина (PKCE + редирект на /api/sso/authorize). `returnPath` — путь
 * внутри NET, должен начинаться с одного `/` (защита от open-redirect).
 * `opts.force` — перебить 5-секундное окно либы; обязателен для явных действий
 * человека («Войти заново»), иначе кнопка молчит сразу после входа, начатого
 * перехватчиком.
 *
 * ⚠ Из скрытой страницы (фоновая вкладка, свёрнутое окно) либа с 0.12 никуда не
 * уходит: переход откладывается до показа, а промис резолвится сразу. Значит
 * resolve здесь НЕ означает «редирект начался» — держите экран «Входим…».
 */
export function redirectToLogin(
  returnPath?: string,
  opts?: StartLoginOptions,
): Promise<void> {
  let final = returnPath ?? window.location.pathname + window.location.search;
  // `//evil.com` браузер понимает как адрес чужого хоста. Либа с 0.12 такой
  // путь отбрасывает сама, здесь — для единообразия.
  if (!final.startsWith("/") || final.startsWith("//")) final = "/admin";
  return authClient.startLogin(final, opts);
}

/**
 * Fire-and-forget вариант для мест, где промис ждать нечем (лоадеры роутера,
 * onClick). Ошибку не глотаем — она уходит в `reportLoginFailure`.
 */
export function beginLogin(
  returnPath?: string,
  opts?: StartLoginOptions,
): void {
  void redirectToLogin(returnPath, opts).catch(reportLoginFailure);
}

/**
 * Колбэк: обменять code на токены. Возвращает сохранённый returnPath.
 *
 * `search` — параметром, а не из `window.location`: либа стирает `?code=` из
 * адреса (на `MissingVerifierError` — синхронно, до throw), а StrictMode
 * вызывает effect дважды. Второй вызов с уже пустым адресом получил бы
 * «no authorization code in callback» вместо закешированного результата обмена.
 */
export function handleSsoCallback(
  search: string = window.location.search,
): Promise<{ returnPath: string | null }> {
  return authClient.handleCallback(search);
}

/**
 * Logout: revoke продуктовой сессии + /api/sso/logout (гасит SSO-cookie) +
 * редирект. Без redirect — просто чистим локально (без revoke).
 *
 * ⚠ `logout(true)` с 0.12 запирает `startLogin` в этом документе (флаг
 * `loggingOut` в либе; снимается только возвратом «Назад» из bfcache). Поэтому
 * для «войти заново» нужен `logout(false)` + `beginLogin(..., { force: true })`.
 */
export async function logout(redirect = true): Promise<void> {
  if (redirect) {
    await authClient.logout();
  } else {
    await store.clear();
  }
}

// ─── Axios instance ──────────────────────────────────────────────────────

export const api: AxiosInstance = axios.create({
  baseURL: "/net/api",
  withCredentials: true,
  timeout: 15000,
});

// Bearer + 401→refresh→retry — из либы. X-Target-Tenant (только net:admin,
// platform-staff) добавляем через onRequest-хук, чтобы не плодить интерцепторы.
attachAxiosAuth(api, authClient, {
  onRequest: (config) => {
    const targetTenant = getTargetTenant();
    if (
      targetTenant &&
      typeof config.url === "string" &&
      config.url.startsWith("/v1/admin/")
    ) {
      config.headers = config.headers ?? {};
      config.headers["X-Target-Tenant"] = targetTenant;
    }
  },
});
